import React from "react";
import styled from "styled-components";
import { EVENT_STATUS } from "../../../constants";
import { color, radius, fontFamily } from "../../Theme";

// Status -> token suffix. Colours live in the per-brand token files
// (chip-<status>-bg / -fg / -bar) so each brand keeps its own palette.
const STATUS_TOKEN = {
  [EVENT_STATUS.ACTIVE]: "active",
  [EVENT_STATUS.CHECKED_IN]: "checked-in",
  [EVENT_STATUS.COMPLETED]: "completed",
  [EVENT_STATUS.CANCELLED]: "cancelled",
  [EVENT_STATUS.MISSED]: "missed",
};

// Style for an event chip: tinted background, text colour and a left bar.
// A user-picked event colour (active events only) is shown as a solid chip.
export function getChipStyle(status, customColor) {
  const token = STATUS_TOKEN[status];
  if (!token || (status === EVENT_STATUS.ACTIVE && customColor)) {
    const base = customColor || color(token ? "event-active" : "primary");
    return { backgroundColor: base, color: "#FFFFFF", borderColor: base };
  }
  return {
    backgroundColor: color(`chip-${token}-bg`),
    color: color(`chip-${token}-fg`),
    borderColor: color(`chip-${token}-bar`),
    strike: status === EVENT_STATUS.CANCELLED,
    hatch: status === EVENT_STATUS.MISSED,
  };
}

const Chip = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: block;
  padding: 4px 8px;
  line-height: ${(p) => (p.$fixed ? p.$height - 8 : 22)}px;
  min-height: ${(p) => p.$height}px;
  height: ${(p) => (p.$fixed ? `${p.$height}px` : "auto")};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  font-family: ${() => fontFamily()};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  border-radius: ${() => radius("small")}px;
  border-left: 3px solid ${(p) => p.$s.borderColor};
  background-color: ${(p) => p.$s.backgroundColor};
  color: ${(p) => p.$s.color};
  text-decoration: ${(p) => (p.$s.strike ? "line-through" : "none")};
  ${(p) =>
    p.$s.hatch
      ? "background-image: repeating-linear-gradient(-45deg, rgba(0, 0, 0, 0.1) 0, rgba(0, 0, 0, 0.1) 3px, transparent 3px, transparent 7px);"
      : ""}
  &:hover {
    filter: brightness(0.96);
  }
  &:focus-visible {
    outline: 2px solid ${() => color("primary")};
    outline-offset: 1px;
  }
`;

export default function EventChip({ eventStyle, tooltip, onClick, height = 32, fixed = false, children }) {
  const s = eventStyle ?? getChipStyle(null);
  return (
    <Chip
      $s={s}
      $height={height}
      $fixed={fixed}
      role="button"
      tabIndex={0}
      title={tooltip}
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick && onClick(e);
        }
      }}
    >
      {children}
    </Chip>
  );
}
